/**
 * Element UI 表格拖拽滚动指令
 * 使用方式：
 *   <el-table v-drag-scroll>                    // 启用拖拽
 *   <el-table v-drag-scroll="{ speed: 2 }">     // 自定义速度
 *   <el-table v-drag-scroll="false">            // 禁用拖拽
 *   <el-table v-drag-scroll="{ enabled: false }"> // 禁用拖拽
 *
 * 配置参数：
 *   - enabled: 是否启用拖拽，默认 true
 *   - speed: 拖拽速度系数，默认 1.5
 */

export default {
  inserted (el, binding) {
    // 解析配置参数
    let options = binding.value
    if (typeof options === 'boolean') {
      options = { enabled: options }
    } else if (typeof options === 'undefined') {
      options = { enabled: true }
    } else {
      options = options || {}
    }

    const enabled = options.enabled !== false
    const speed = options.speed || 1.5

    // 获取表格 body-wrapper
    const tableWrapper = el.querySelector('.el-table__body-wrapper')
    if (!tableWrapper) {
      console.warn('[v-drag-scroll] 未找到 .el-table__body-wrapper 元素')
      return
    }

    // 如果禁用，则不绑定事件
    if (!enabled) {
      return
    }

    // 拖拽状态
    let isMouseDown = false
    let isDragging = false
    let startX = 0
    let scrollLeft = 0
    let startMouseX = 0
    let startMouseY = 0
    const DRAG_THRESHOLD = 5 // 拖拽阈值（像素）

    // 判断元素是否可点击（不应触发拖拽）
    const isClickableElement = (target) => {
      const tagName = target.tagName.toLowerCase()
      if (['button', 'a', 'input', 'textarea', 'select'].includes(tagName)) {
        return true
      }
      // 检查是否在可点击元素内部
      if (target.closest('button') ||
          target.closest('a') ||
          target.closest('.el-input') ||
          target.closest('.el-button') ||
          target.closest('.el-link') ||
          target.closest('.clickable-name')) {
        return true
      }
      return false
    }

    // mousedown 事件处理
    const handleMouseDown = (e) => {
      // 只响应左键点击
      if (e.button !== 0) return
      // 如果点击的是可点击元素，不触发拖拽
      if (isClickableElement(e.target)) return

      isMouseDown = true
      isDragging = false
      startX = e.pageX - tableWrapper.offsetLeft
      scrollLeft = tableWrapper.scrollLeft
      startMouseX = e.pageX
      startMouseY = e.pageY
    }

    // mousemove 事件处理
    const handleMouseMove = (e) => {
      if (!isMouseDown) return

      // 判断是否超过拖拽阈值
      if (!isDragging) {
        const moveX = Math.abs(e.pageX - startMouseX)
        const moveY = Math.abs(e.pageY - startMouseY)
        if (moveX > DRAG_THRESHOLD || moveY > DRAG_THRESHOLD) {
          isDragging = true
          tableWrapper.style.userSelect = 'none'
        }
      }

      // 只有真正开始拖拽时才阻止默认行为并滚动
      if (isDragging) {
        e.preventDefault()
        const x = e.pageX - tableWrapper.offsetLeft
        const walk = (x - startX) * speed
        tableWrapper.scrollLeft = scrollLeft - walk
      }
    }

    // mouseup 事件处理
    const handleMouseUp = () => {
      isMouseDown = false
      isDragging = false
      tableWrapper.style.userSelect = ''
    }

    // mouseleave 事件处理
    const handleMouseLeave = () => {
      isMouseDown = false
      isDragging = false
      tableWrapper.style.userSelect = ''
    }

    // 绑定事件
    tableWrapper.addEventListener('mousedown', handleMouseDown)
    tableWrapper.addEventListener('mousemove', handleMouseMove)
    tableWrapper.addEventListener('mouseup', handleMouseUp)
    tableWrapper.addEventListener('mouseleave', handleMouseLeave)

    // 保存事件处理函数引用和配置，用于解绑和更新
    el._dragScroll = {
      tableWrapper,
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
      handleMouseLeave,
      options: { enabled: true }
    }
  },

  update (el, binding) {
    // 解析新的配置参数
    let options = binding.value
    if (typeof options === 'boolean') {
      options = { enabled: options }
    } else if (typeof options === 'undefined') {
      options = { enabled: true }
    } else {
      options = options || {}
    }

    const enabled = options.enabled !== false

    // 如果之前没有初始化过，直接返回（由 inserted 处理）
    if (!el._dragScroll) return

    // 获取保存的配置
    const savedOptions = el._dragScroll.options || {}
    const wasEnabled = savedOptions.enabled !== false

    // 如果启用状态没有变化，不做任何操作
    if (enabled === wasEnabled) return

    // 更新保存的配置
    el._dragScroll.options = options

    const { tableWrapper, handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave } = el._dragScroll

    if (enabled) {
      // 从禁用变为启用，绑定事件
      tableWrapper.addEventListener('mousedown', handleMouseDown)
      tableWrapper.addEventListener('mousemove', handleMouseMove)
      tableWrapper.addEventListener('mouseup', handleMouseUp)
      tableWrapper.addEventListener('mouseleave', handleMouseLeave)
    } else {
      // 从启用变为禁用，解绑事件
      tableWrapper.removeEventListener('mousedown', handleMouseDown)
      tableWrapper.removeEventListener('mousemove', handleMouseMove)
      tableWrapper.removeEventListener('mouseup', handleMouseUp)
      tableWrapper.removeEventListener('mouseleave', handleMouseLeave)
    }
  },

  unbind (el) {
    // 解绑事件
    if (el._dragScroll) {
      const { tableWrapper, handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave } = el._dragScroll
      tableWrapper.removeEventListener('mousedown', handleMouseDown)
      tableWrapper.removeEventListener('mousemove', handleMouseMove)
      tableWrapper.removeEventListener('mouseup', handleMouseUp)
      tableWrapper.removeEventListener('mouseleave', handleMouseLeave)
      delete el._dragScroll
    }
  }
}
